import _isFunction from "lodash/isFunction";
import browser from "web/script/utils/browser";
import environment from "web/script/modules/environment";
import storage from "web/script/utils/storage";
import globals from "web/script/modules/globals";
import history from "web/script/utils/history";
import store from "web/redux/store";
import url from "web/script/utils/url";
import userProfiler from "web/script/modules/userprofiler";
import { openProductCardModal, openGenericModal } from "web/redux/ducks/send-to-app-modal";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import globalEvents from "web/script/utils/global-events";

interface UseProductClickArgs {
    id?: string;
    productUrl?: string;
    productImageUrl?: string;
    inStock?: boolean;
    shouldRedirect?: boolean;
    overlayEnabled?: boolean;
    useOverlayOnMobile?: boolean;
    productUid?: string;
    productSlug?: string;
    reason?: string;
    linkId?: string;
    sendAnalytics?: () => void;
    href?: string;
    isLeadLink?: boolean;
    openInNewTab?: boolean;
}

interface UseProductClickResponse {
    onClick: (event) => any;
}

function extractEventCount(state): any {
    return {
        eventCountBlock: state.eventCountTrackerReducer.shouldBlockAll,
        semiHardBlock: state.eventCountTrackerReducer.shouldBlockSemiHard,
    };
}

/**
 * Returns true if a new tab should be used for the event
 */
function _isNewTab(event): void {
    return (browser.mac && event.metaKey) || (!browser.mac && event.ctrlKey) || event.which === 2;
}

const DEFAULT_PRODUCT_CLICK_ARGS = {
    inStock: false,
    overlayEnabled: false,
    useOverlayOnMobile: false,
};

// useProductClick is a hook counterpart for the legacy product-click-mixin typically used in Baustein components
// use with caution as code may diverge between the two versions as long as both exist
export function useProductClick(props: UseProductClickArgs): UseProductClickResponse {
    const args = { ...DEFAULT_PRODUCT_CLICK_ARGS, ...props };
    const {
        id,
        productUrl,
        productImageUrl,
        inStock,
        shouldRedirect,
        overlayEnabled,
        useOverlayOnMobile,
        productSlug,
        reason,
        linkId,
        sendAnalytics,
        href,
        isLeadLink,
        openInNewTab,
    } = args;

    function _redirectWithReason(event, newTab = false): void {
        let parsed = url.parse(href as string);

        event.preventDefault();

        if (reason) {
            parsed.searchParams.set("reason", reason);
        }

        if (newTab) {
            globals.window.open(url.unparse(parsed), "_blank");
            return;
        }

        globals.window.location = url.unparse(parsed) as any;
    }

    /**
     * Product click event handler that will either redirect or open a new tab to product page.
     */
    function _onClickRedirect(event): void {
        if (event.target.classList.contains("show-more-button")) {
            return;
        }

        let newTab = _isNewTab(event) as any;

        globalEvents.trigger("product-card-selected", event);
        _redirectWithReason(event, newTab);
    }

    /**
     * Product click event handler that will either open an overlay, new tab or redirect to product page.
     */
    function _onClick(event): void {
        if (event.target.classList.contains("show-more-button")) {
            return;
        }

        event.preventDefault();
        if (_isFunction(sendAnalytics)) {
            sendAnalytics();
        }

        let overlayEnabledOption = overlayEnabled;
        let overlayOnMobileOption = useOverlayOnMobile;

        let isOverlayEnabled =
            overlayEnabledOption &&
            productSlug &&
            browser.supportsHistory &&
            !event.metaKey &&
            // Feature flag to use overlay on mobile too
            ((overlayOnMobileOption && browser.getViewport() === browser.VIEWPORTS.MOBILE) ||
                (!browser.ios &&
                    !browser.android &&
                    browser.getViewport() === browser.VIEWPORTS.DESKTOP));

        globalEvents.trigger("product-card-selected", event);

        const { eventCountBlock, semiHardBlock } = extractEventCount(store.getState());
        const shouldSemiBlock =
            environment.getFeature("mg_semi_hard_block_feeds_show_more") && semiHardBlock;

        const eventCounter = storage.get("eventCounter", false, true);
        const stopAllFurtherBlocks =
            environment.getFeature("mg_second_interaction_product_card_soft_block") &&
            eventCounter.hasBeenDismissed;

        if (
            browser.ios &&
            !userProfiler.isLoggedIn() &&
            eventCountBlock &&
            environment.get("pageType") === "feed"
        ) {
            store.dispatch(
                openGenericModal({
                    modalType: "event_counter",
                    product: {
                        productUrl: productUrl as string,
                        productImageUrl: productImageUrl as string,
                        inStock: inStock as boolean,
                    },
                    paywalledClassName: "product-card",
                })
            );
            return;
        }

        const productFeedsCapture = environment.getFeature("mg_feeds_product_card_android_block");
        const hasBeenBlockedOnce = !!storage.get("hasBeenBlockedProductCard", false, true);

        const shouldBlockGeneralFeed =
            environment.getFeature("mg_block_product_card") &&
            environment.getFeature("mg_block_product_card") !== "no_block";
        const shouldBlockRelatedFeed =
            (environment.getFeature("mg_block_is_pdp_related_product_card") &&
                environment.getFeature("mg_block_is_pdp_related_product_card") !== "no_block") ||
            (environment.getFeature("mg_android_block_is_pdp_related_product_card") &&
                environment.getFeature("mg_android_block_is_pdp_related_product_card") !==
                    "no_block");
        const shouldBlockProductFeeds =
            productFeedsCapture &&
            (productFeedsCapture === "hard_block" ||
                (productFeedsCapture === "soft_block" && !hasBeenBlockedOnce));

        if (
            browser.ios &&
            !userProfiler.isLoggedIn() &&
            !stopAllFurtherBlocks &&
            (shouldBlockGeneralFeed || shouldBlockRelatedFeed || shouldSemiBlock)
        ) {
            store.dispatch(
                openProductCardModal({
                    productUrl: productUrl as string,
                    productImageUrl: productImageUrl as string,
                    inStock: inStock as boolean,
                })
            );
            return;
        }

        if (
            browser.android &&
            !userProfiler.isLoggedIn() &&
            (shouldBlockRelatedFeed || shouldBlockProductFeeds)
        ) {
            if (shouldBlockProductFeeds) {
                storage.set("hasBeenBlockedProductCard", "true", true);
            }
            const captureType = shouldBlockProductFeeds ? "feeds_product_card" : "related_product";
            store.dispatch(
                openCustomerCaptureOverlay({
                    productId: id,
                    captureType,
                    next: productUrl,
                })
            );
            return;
        }

        // if the overlay isn't enabled, and the target is to open a new tab, then bail out here
        if (!isOverlayEnabled && openInNewTab) {
            // the lead-link click handler also calls `preventDefault` and calls window.open. we don't
            // want to to it twice.
            if (!isLeadLink) {
                globals.window.open(href);
            }
            return;
        }

        let newTab = _isNewTab(event) as any;

        if (
            !isOverlayEnabled ||
            newTab ||
            !reason ||
            browser.getViewport() === browser.VIEWPORTS.MOBILE
        ) {
            _redirectWithReason(event, newTab);
        } else {
            let queryParams = {
                reason: reason,
                link_id: linkId,
            };

            let url = new URL(href as string, window.location.href);
            for (let [k, v] of Object.entries(queryParams)) {
                url.searchParams.set(k, v as string);
            }

            history.pushState(url);

            globalEvents.trigger("open-overlay", {
                url: href,
                queryParams,
            });
        }
    }

    return { onClick: shouldRedirect ? _onClickRedirect : _onClick };
}
